import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faCheck,
  faMagnifyingGlass,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {HttpErrorResponse} from '@angular/common/http';
import {UiButtonComponent, UiInputComponent, UiToastService} from '../../shared/ui';
import {ImageSearchService} from '../../services/image-search/image-search.service';
import {
  ImageSearchHit,
  ImageSearchProvider,
  ImageSearchResult,
  ImageSearchSubject,
} from '../../models/image-search';
import {AppLogger, AppLoggerService} from '../../core/logging';

/**
 * One result, plus the object URL its preview bytes were loaded into. The
 * preview endpoint is auth-protected, so results cannot be rendered by binding
 * `preview_url` directly.
 */
interface HitView {
  hit: ImageSearchHit;
  objectUrl: string | null;
  /** Preview fetch failed — the tile shows a placeholder instead of hanging. */
  failed: boolean;
}

/**
 * Search a provider for a picture and import the one the user picks. Shared by
 * the product and recipe image managers: only the subject and the owning id
 * differ, everything else (paging, previews, attribution, error states) is the
 * same flow.
 */
@Component({
  selector: 'app-image-search-panel',
  imports: [FaIconComponent, UiButtonComponent, UiInputComponent],
  templateUrl: './image-search-panel.component.html',
  styleUrl: './image-search-panel.component.scss',
})
export class ImageSearchPanelComponent implements OnInit, OnDestroy {
  private readonly log: AppLogger;

  /** Whether we are picking an image for a product or for a recipe. */
  @Input() subject: ImageSearchSubject = 'product';
  /** Id of the product or recipe the image will be attached to. */
  @Input() entityId!: number;
  /** Label of the "make this the main picture" action, which differs per subject. */
  @Input() primaryActionLabel = 'Use as primary';

  /** Emitted after an image was imported, so the manager can reload its list. */
  @Output() imported = new EventEmitter<void>();
  /** Emitted when the user backs out without picking anything. */
  @Output() cancelled = new EventEmitter<void>();

  query = '';
  /** The query the backend actually used, echoed so the box can be pre-filled. */
  effectiveQuery = '';
  /**
   * Provider the user explicitly pinned via the source picker. Empty means
   * "let the backend walk its chain" — which is the default, so a provider with
   * no hits falls through to the next one instead of the UI locking onto
   * whichever answered first.
   */
  pinnedProvider = '';
  /** Provider that produced the results currently on screen. */
  answeringProvider = '';
  sources: ImageSearchProvider[] = [];

  hits: HitView[] = [];
  selected: HitView | null = null;

  page = 1;
  hasMore = false;

  isSearching = false;
  isLoadingMore = false;
  isImporting = false;
  /** Set when search is unavailable (no provider configured, HTTP 501). */
  unavailable = false;
  errorMessage = '';
  /** True once a search has completed, so "no results" is only shown after one. */
  hasSearched = false;

  faSearch = faMagnifyingGlass;
  faSpinner = faSpinner;
  faCheck = faCheck;
  faWarning = faTriangleExclamation;
  faBack = faArrowLeft;

  constructor(
    private imageSearchService: ImageSearchService,
    private toast: UiToastService,
    appLogger: AppLoggerService,
  ) {
    this.log = appLogger.createForClass(ImageSearchPanelComponent);
  }

  ngOnInit(): void {
    // Open with results already on screen: an empty query makes the backend
    // derive one from the product's name and brand, or the recipe's title.
    this.runSearch(1);
  }

  ngOnDestroy(): void {
    this.releasePreviews();
  }

  get canImport(): boolean {
    return !!this.selected && !this.isImporting;
  }

  get placeholder(): string {
    return this.subject === 'recipe' ? 'Search for a dish photo' : 'Search for a product image';
  }

  providerName(key: string): string {
    return this.sources.find(s => s.key === key)?.name ?? key;
  }

  onQueryChange(value: string): void {
    this.query = value;
  }

  submitSearch(): void {
    this.runSearch(1);
  }

  loadMore(): void {
    if (!this.hasMore || this.isLoadingMore || this.isSearching) {
      return;
    }
    this.runSearch(this.page + 1, true);
  }

  selectHit(view: HitView): void {
    if (view.failed) {
      return;
    }
    this.selected = view;
  }

  cancel(): void {
    this.cancelled.emit();
  }

  /**
   * Pin a source, or unpin it by tapping the active one — going back to
   * "automatic" lets the backend fall through its chain again.
   */
  toggleProvider(key: string): void {
    this.pinnedProvider = this.pinnedProvider === key ? '' : key;
    this.runSearch(1);
  }

  importSelected(isPrimary: boolean): void {
    const view = this.selected;
    if (!view || this.isImporting) {
      return;
    }

    this.isImporting = true;
    this.log.debug('Importing searched image', {
      subject: this.subject,
      entityId: this.entityId,
      provider: view.hit.provider,
      isPrimary,
    });

    this.imageSearchService
      .import(this.subject, this.entityId, view.hit.candidate_id, isPrimary)
      .subscribe({
        next: () => {
          this.isImporting = false;
          this.toast.success('Image added');
          this.imported.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.isImporting = false;
          this.log.error('Failed to import searched image', err, {
            subject: this.subject,
            entityId: this.entityId,
          });

          // A candidate token lives ~15 minutes. Expiry is the one failure the
          // user can recover from without thinking, so re-run the search for
          // them instead of showing an error they cannot act on.
          if (err.status === 410) {
            this.toast.warning('Those results expired — searching again');
            this.runSearch(1);
            return;
          }

          this.toast.error(this.messageForError(err, 'Could not add that image'));
        },
      });
  }

  private runSearch(page: number, append = false): void {
    if (!this.entityId) {
      return;
    }

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isSearching = true;
      this.selected = null;
    }
    this.errorMessage = '';
    this.unavailable = false;

    this.imageSearchService
      .search(this.subject, this.entityId, {
        q: this.query,
        page,
        provider: this.pinnedProvider,
      })
      .subscribe({
        next: (result: ImageSearchResult) => {
          this.isSearching = false;
          this.isLoadingMore = false;
          this.hasSearched = true;
          this.applyResult(result, append);
        },
        error: (err: HttpErrorResponse) => {
          this.isSearching = false;
          this.isLoadingMore = false;
          this.hasSearched = true;
          this.log.error('Image search failed', err, {
            subject: this.subject,
            entityId: this.entityId,
            page,
          });

          // 501 means no provider is configured for this subject — a deployment
          // state, not a user error, so it gets its own quiet panel.
          if (err.status === 501) {
            this.unavailable = true;
            return;
          }

          this.errorMessage = this.messageForError(err, 'Search failed');
        },
      });
  }

  private applyResult(result: ImageSearchResult, append: boolean): void {
    if (result.sources?.length) {
      this.sources = result.sources;
    }
    this.page = result.page || 1;
    this.hasMore = !!result.has_more;
    this.effectiveQuery = result.query ?? '';
    this.answeringProvider = result.provider ?? '';

    // Pre-fill the box with the query the backend derived, so refining it means
    // editing real text rather than guessing what was searched.
    if (!this.query && this.effectiveQuery) {
      this.query = this.effectiveQuery;
    }

    const views: HitView[] = (result.results ?? []).map(hit => ({
      hit,
      objectUrl: null,
      failed: false,
    }));

    if (append) {
      this.hits = [...this.hits, ...views];
    } else {
      this.releasePreviews();
      this.hits = views;
    }

    views.forEach(view => this.loadPreview(view));
  }

  private loadPreview(view: HitView): void {
    this.imageSearchService.preview(view.hit.candidate_id).subscribe({
      next: blob => {
        if (!blob || blob.size === 0) {
          this.markFailed(view);
          return;
        }
        const url = URL.createObjectURL(blob);
        this.hits = this.hits.map(v =>
          v.hit.candidate_id === view.hit.candidate_id ? {...v, objectUrl: url} : v,
        );
        // Keep the selection pointing at the refreshed object.
        if (this.selected?.hit.candidate_id === view.hit.candidate_id) {
          this.selected = this.hits.find(v => v.hit.candidate_id === view.hit.candidate_id) ?? null;
        }
      },
      error: err => {
        this.log.error('Failed to load search preview', err, {provider: view.hit.provider});
        this.markFailed(view);
      },
    });
  }

  private markFailed(view: HitView): void {
    this.hits = this.hits.map(v =>
      v.hit.candidate_id === view.hit.candidate_id ? {...v, failed: true} : v,
    );
  }

  private releasePreviews(): void {
    for (const view of this.hits) {
      if (view.objectUrl) {
        URL.revokeObjectURL(view.objectUrl);
      }
    }
  }

  private messageForError(err: HttpErrorResponse, fallback: string): string {
    switch (err.status) {
      case 503:
        return 'The image provider is busy right now — try again in a minute';
      case 422:
        return 'That image could not be used (wrong format or too small)';
      case 403:
        return 'You do not have permission to change this';
      default:
        return fallback;
    }
  }
}
